import { SupportedChainId } from 'config/chains'
import { Address, decodeFunctionData, Hex } from 'viem'

// ============================================================================
// CONSTANTS AND ADDRESSES
// ============================================================================

const NATIVE_ADDRESS = '0x0000000000000000000000000000000000000000' as Address

const KODIAK_META_ROUTER_ADDRESSES: { [chainId: number]: Address } = {
  [SupportedChainId.BERA]: '0x43Dac637c4383f91B4368041E7A8687da3806Cae',
  [SupportedChainId.ROBINHOOD]: '0xfd27998fa0eaB1A6372Db14Afd4bF7c4a58C5364',
}

const KODIAK_ROUTER_ADDRESSES: { [chainId: number]: Address } = {
  [SupportedChainId.BERA]: '0xe301E48F77963D3F7DbD2a4796962Bd7f3867Fb4',
  [SupportedChainId.ROBINHOOD]: '0xE3CA4E662C0cc417D2fD46Fa02f00f4DDe1076eD',
}

const WRAPPED_NATIVE_ADDRESSES: { [chainId: number]: Address } = {
  [SupportedChainId.BERA]: '0x6969696969696969696969696969696969696969',
  [SupportedChainId.ROBINHOOD]: '0x0Bd7D308f8E1639FAb988df18A8011f41EAcAD73',
}

const FUNCTION_SIGNATURES = {
  SWAP: '0xd2644d14', // MetaRouter swap function
  MULTICALL: '0x5ae401dc', // Router multicall function
  EXACT_INPUT_SINGLE: '0x04e45aaf', // Router exactInputSingle function
} as const

// ============================================================================
// ABI DEFINITIONS
// ============================================================================

const KODIAK_META_ROUTER_ABI = [
  {
    inputs: [
      {
        internalType: 'struct KodiakMetaRouter.InputAmount',
        name: 'input',
        type: 'tuple',
        components: [
          { internalType: 'address', name: 'token', type: 'address' },
          { internalType: 'bool', name: 'wrap', type: 'bool' },
          { internalType: 'uint256', name: 'amount', type: 'uint256' },
        ],
      },
      {
        internalType: 'struct KodiakMetaRouter.OutputAmount',
        name: 'output',
        type: 'tuple',
        components: [
          { internalType: 'address', name: 'token', type: 'address' },
          { internalType: 'bool', name: 'unwrap', type: 'bool' },
          { internalType: 'uint256', name: 'minAmountOut', type: 'uint256' },
          { internalType: 'address', name: 'receiver', type: 'address' },
        ],
      },
      {
        internalType: 'struct KodiakMetaRouter.SwapData',
        name: 'swapData',
        type: 'tuple',
        components: [
          { internalType: 'address', name: 'router', type: 'address' },
          { internalType: 'bytes', name: 'data', type: 'bytes' },
        ],
      },
      {
        internalType: 'struct KodiakMetaRouter.FeeData',
        name: 'feeData',
        type: 'tuple',
        components: [
          { internalType: 'uint256', name: 'feeQuote', type: 'uint256' },
          { internalType: 'uint16', name: 'surplusFeeBps', type: 'uint16' },
          { internalType: 'uint16', name: 'refCode', type: 'uint16' },
          { internalType: 'uint16', name: 'referrerFeeBps', type: 'uint16' },
        ],
      },
    ],
    stateMutability: 'payable',
    type: 'function',
    name: 'swap',
  },
] as const

const KODIAK_ROUTER_ABI = [
  {
    inputs: [
      { internalType: 'uint256', name: 'deadline', type: 'uint256' },
      { internalType: 'bytes[]', name: 'data', type: 'bytes[]' },
    ],
    name: 'multicall',
    outputs: [{ internalType: 'bytes[]', name: 'results', type: 'bytes[]' }],
    stateMutability: 'payable',
    type: 'function',
  },
  {
    inputs: [
      {
        components: [
          { internalType: 'address', name: 'tokenIn', type: 'address' },
          { internalType: 'address', name: 'tokenOut', type: 'address' },
          { internalType: 'uint24', name: 'fee', type: 'uint24' },
          { internalType: 'address', name: 'recipient', type: 'address' },
          { internalType: 'uint256', name: 'amountIn', type: 'uint256' },
          { internalType: 'uint256', name: 'amountOutMinimum', type: 'uint256' },
          { internalType: 'uint160', name: 'sqrtPriceLimitX96', type: 'uint160' },
        ],
        internalType: 'struct IV3SwapRouter.ExactInputSingleParams',
        name: 'params',
        type: 'tuple',
      },
    ],
    name: 'exactInputSingle',
    outputs: [{ internalType: 'uint256', name: 'amountOut', type: 'uint256' }],
    stateMutability: 'payable',
    type: 'function',
  },
  {
    inputs: [
      {
        components: [
          { internalType: 'bytes', name: 'path', type: 'bytes' },
          { internalType: 'address', name: 'recipient', type: 'address' },
          { internalType: 'uint256', name: 'amountIn', type: 'uint256' },
          { internalType: 'uint256', name: 'amountOutMinimum', type: 'uint256' },
        ],
        internalType: 'struct IV3SwapRouter.ExactInputParams',
        name: 'params',
        type: 'tuple',
      },
    ],
    name: 'exactInput',
    outputs: [{ internalType: 'uint256', name: 'amountOut', type: 'uint256' }],
    stateMutability: 'payable',
    type: 'function',
  },
  {
    inputs: [
      { internalType: 'uint256', name: 'amountIn', type: 'uint256' },
      { internalType: 'uint256', name: 'amountOutMin', type: 'uint256' },
      { internalType: 'address[]', name: 'path', type: 'address[]' },
      { internalType: 'address', name: 'to', type: 'address' },
    ],
    name: 'swapExactTokensForTokens',
    outputs: [{ internalType: 'uint256', name: 'amountOut', type: 'uint256' }],
    stateMutability: 'payable',
    type: 'function',
  },
  {
    inputs: [{ internalType: 'uint256', name: 'value', type: 'uint256' }],
    name: 'wrapETH',
    outputs: [],
    stateMutability: 'payable',
    type: 'function',
  },
  {
    inputs: [
      { internalType: 'uint256', name: 'amountMinimum', type: 'uint256' },
      { internalType: 'address', name: 'recipient', type: 'address' },
    ],
    name: 'unwrapWETH9',
    outputs: [],
    stateMutability: 'payable',
    type: 'function',
  },
] as const

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type QuoteType = 'kodiakx' | 'kodiak-native' | 'unknown'

export interface ValidationParams {
  chainId: SupportedChainId
  tokenInAddress: string
  tokenOutAddress: string
  amountIn: string | bigint
  expectedAmountOut: string | bigint
  recipient: string
  slippageTolerance: number
  maxSlippageVariance?: number
}

export interface ValidationResult {
  isValid: boolean
  errors: string[]
  warnings: string[]
  quoteType: QuoteType
  details: {
    recipientMatch: boolean
    tokenInMatch: boolean
    tokenOutMatch: boolean
    amountInMatch: boolean
    minAmountOutValid: boolean
    slippageCalculation: {
      expected: bigint
      actual: bigint
      difference: bigint
      percentageDiff: number
    }
    fees?: {
      surplusFeeBps: number
      referrerFeeBps: number
      refCode: number
    }
  }
}

interface KodiakXSwapData {
  input: {
    token: Address
    wrap: boolean
    amount: bigint
  }
  output: {
    token: Address
    unwrap: boolean
    minAmountOut: bigint
    receiver: Address
  }
  swapData: {
    router: Address
    data: Hex
  }
  feeData: {
    feeQuote: bigint
    surplusFeeBps: number
    referrerFeeBps: number
    refCode: number
  }
}

interface KodiakNativeSwapData {
  deadline: bigint
  swapCalls: Array<{
    functionName: string
    tokenIn: string
    tokenOut: string
    fee: number
    recipient: string
    amountIn: bigint
    amountOutMinimum: bigint
    sqrtPriceLimitX96: bigint
  }>
  totalAmountIn: bigint
  totalAmountOutMinimum: bigint
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

function toBigInt(value: any): bigint {
  if (typeof value === 'bigint') {
    return value
  }
  if (typeof value === 'object' && value !== null && 'toString' in value) {
    return BigInt(value.toString())
  }
  return BigInt(value.toString())
}

function normalizeAddress(address: string): string {
  return address.toLowerCase()
}

function isNativeToken(address: string): boolean {
  const addr = normalizeAddress(address)
  return addr === NATIVE_ADDRESS || addr === 'native'
}

function isWrappedNative(address: string, chainId: SupportedChainId): boolean {
  return normalizeAddress(address) === normalizeAddress(WRAPPED_NATIVE_ADDRESSES[chainId])
}

// ============================================================================
// QUOTE TYPE DETECTION
// ============================================================================

export function detectQuoteType(targetAddress: string, calldata: string, chainId: SupportedChainId): QuoteType {
  const address = normalizeAddress(targetAddress)
  const signature = calldata.slice(0, 10)

  if (address === normalizeAddress(KODIAK_META_ROUTER_ADDRESSES[chainId])) {
    return 'kodiakx'
  }

  if (address === normalizeAddress(KODIAK_ROUTER_ADDRESSES[chainId])) {
    return 'kodiak-native'
  }

  if (signature === FUNCTION_SIGNATURES.SWAP) {
    return 'kodiakx'
  }

  if (signature === FUNCTION_SIGNATURES.MULTICALL) {
    return 'kodiak-native'
  }

  return 'unknown'
}

// ============================================================================
// TOKEN VALIDATION
// ============================================================================

function validateTokenAddress(
  expectedAddress: string,
  actualAddress: string,
  wrapFlag: boolean,
  chainId: SupportedChainId,
  isOutput = false
): { isValid: boolean; error?: string } {
  const expected = normalizeAddress(expectedAddress)
  const actual = normalizeAddress(actualAddress)

  const isExpectedNative = isNativeToken(expected)
  const isExpectedWrappedNative = isWrappedNative(expected, chainId)
  const isActualWrappedNative = isWrappedNative(actual, chainId)

  if (isExpectedNative) {
    if (isActualWrappedNative) {
      return { isValid: true }
    }

    return {
      isValid: false,
      error: `Native BERA cannot be swapped as ${actualAddress}`,
    }
  }

  if (isExpectedWrappedNative) {
    if (isActualWrappedNative && wrapFlag === false) {
      return { isValid: true }
    }

    return {
      isValid: false,
      error: `Wrapped native selected directly should not have ${isOutput ? 'unwrap' : 'wrap'}=true`,
    }
  }

  if (expected === actual && wrapFlag === false) {
    return { isValid: true }
  }

  return {
    isValid: false,
    error: `Token mismatch: expected ${expectedAddress}, got ${actualAddress} with ${
      isOutput ? 'unwrap' : 'wrap'
    }=${wrapFlag}`,
  }
}

// ============================================================================
// SLIPPAGE VALIDATION
// ============================================================================

function validateSlippage(
  expectedAmountOut: bigint,
  actualMinAmountOut: bigint,
  slippageTolerance: number,
  maxVariance = 0.1
): {
  isValid: boolean
  expected: bigint
  actual: bigint
  difference: bigint
  percentageDiff: number
  error?: string
} {
  const slippageBps = BigInt(Math.floor(slippageTolerance * 100))
  const expectedMinAmountOut = expectedAmountOut - (expectedAmountOut * slippageBps) / 10000n

  const rawDifference =
    actualMinAmountOut > expectedMinAmountOut
      ? actualMinAmountOut - expectedMinAmountOut
      : expectedMinAmountOut - actualMinAmountOut
  const difference = rawDifference

  let percentageDiff = 0
  if (expectedMinAmountOut > 0n) {
    percentageDiff = Number((difference * 10000n) / expectedMinAmountOut) / 100
  }

  const maxVarianceBps = BigInt(Math.floor(maxVariance * 100))
  const varianceThreshold = (expectedMinAmountOut * maxVarianceBps) / 10000n

  const isValid = difference <= varianceThreshold
  let error: string | undefined

  if (!isValid) {
    if (actualMinAmountOut < expectedMinAmountOut) {
      error = `Minimum amount out too low: expected ≥${expectedMinAmountOut.toString()}, got ${actualMinAmountOut.toString()}`
    } else {
      error = `Minimum amount out higher than expected: expected ${expectedMinAmountOut.toString()}, got ${actualMinAmountOut.toString()}`
    }
  }

  return {
    isValid,
    expected: expectedMinAmountOut,
    actual: actualMinAmountOut,
    difference,
    percentageDiff,
    error,
  }
}

// ============================================================================
// KODIAKX (META ROUTER) DECODER AND VALIDATOR
// ============================================================================

function decodeKodiakXSwap(calldata: string): KodiakXSwapData {
  const { functionName, args } = decodeFunctionData({
    abi: KODIAK_META_ROUTER_ABI,
    data: calldata as Hex,
  })

  if (functionName !== 'swap') {
    throw new Error(`Expected swap function, got ${functionName}`)
  }

  // Viem returns arguments as a simple array, indexed by position
  const [input, output, swapData, feeData] = args as [
    { token: Address; wrap: boolean; amount: bigint },
    { token: Address; unwrap: boolean; minAmountOut: bigint; receiver: Address },
    { router: Address; data: Hex },
    { feeQuote: bigint; surplusFeeBps: number; referrerFeeBps: number; refCode: number }
  ]

  return {
    input: {
      token: input.token,
      wrap: input.wrap,
      amount: input.amount,
    },
    output: {
      token: output.token,
      unwrap: output.unwrap,
      minAmountOut: output.minAmountOut,
      receiver: output.receiver,
    },
    swapData: {
      router: swapData.router,
      data: swapData.data,
    },
    feeData: {
      feeQuote: feeData.feeQuote,
      surplusFeeBps: feeData.surplusFeeBps,
      referrerFeeBps: feeData.referrerFeeBps,
      refCode: feeData.refCode,
    },
  }
}

function validateKodiakXSwap(calldata: string, params: ValidationParams): ValidationResult {
  const result: ValidationResult = {
    isValid: true,
    errors: [],
    warnings: [],
    quoteType: 'kodiakx',
    details: {
      recipientMatch: false,
      tokenInMatch: false,
      tokenOutMatch: false,
      amountInMatch: false,
      minAmountOutValid: false,
      slippageCalculation: {
        expected: 0n,
        actual: 0n,
        difference: 0n,
        percentageDiff: 0,
      },
    },
  }

  try {
    const decoded = decodeKodiakXSwap(calldata)

    const recipientMatch =
      normalizeAddress(decoded.output.receiver) === normalizeAddress(params.recipient) ||
      normalizeAddress(decoded.output.receiver) === normalizeAddress(KODIAK_META_ROUTER_ADDRESSES[params.chainId])

    result.details.recipientMatch = recipientMatch

    if (!recipientMatch) {
      result.errors.push(`SECURITY: Recipient mismatch! Expected ${params.recipient}, got ${decoded.output.receiver}`)
      result.isValid = false
    }

    // const tokenInValidation = validateTokenAddress(params.tokenInAddress, decoded.input.token, decoded.input.wrap)
    // result.details.tokenInMatch = tokenInValidation.isValid

    // if (!tokenInValidation.isValid) {
    //   result.errors.push(`Input token validation failed: ${tokenInValidation.error}`)
    //   result.isValid = false
    // }

    // const tokenOutValidation = validateTokenAddress(
    //   params.tokenOutAddress,
    //   decoded.output.token,
    //   decoded.output.unwrap,
    //   true
    // )
    // result.details.tokenOutMatch = tokenOutValidation.isValid

    // if (!tokenOutValidation.isValid) {
    //   result.errors.push(`Output token validation failed: ${tokenOutValidation.error}`)
    //   result.isValid = false
    // }

    // const expectedAmountIn = toBigNumber(params.amountIn)
    // const amountInMatch = decoded.input.amount.eq(expectedAmountIn)
    // result.details.amountInMatch = amountInMatch

    // if (!amountInMatch) {
    //   result.errors.push(
    //     `Input amount mismatch: expected ${expectedAmountIn.toString()}, got ${decoded.input.amount.toString()}`
    //   )
    //   result.isValid = false
    // }

    // const expectedAmountOut = toBigNumber(params.expectedAmountOut)
    // const slippageValidation = validateSlippage(
    //   expectedAmountOut,
    //   decoded.output.minAmountOut,
    //   params.slippageTolerance,
    //   params.maxSlippageVariance
    // )

    // result.details.slippageCalculation = {
    //   expected: slippageValidation.expected,
    //   actual: slippageValidation.actual,
    //   difference: slippageValidation.difference,
    //   percentageDiff: slippageValidation.percentageDiff,
    // }
    // result.details.minAmountOutValid = slippageValidation.isValid

    // if (!slippageValidation.isValid) {
    //   if (slippageValidation.error) {
    //     result.errors.push(slippageValidation.error)
    //     result.isValid = false
    //   }
    // }

    // result.details.fees = {
    //   surplusFeeBps: decoded.feeData.surplusFeeBps,
    //   referrerFeeBps: decoded.feeData.referrerFeeBps,
    //   refCode: decoded.feeData.refCode,
    // }

    // if (decoded.feeData.surplusFeeBps > 0 || decoded.feeData.referrerFeeBps > 0) {
    //   result.warnings.push(
    //     `Fees: Surplus ${decoded.feeData.surplusFeeBps / 100}%, Referrer ${decoded.feeData.referrerFeeBps / 100}%`
    //   )
    // }
  } catch (error) {
    result.errors.push(`Failed to decode KodiakX calldata: ${error instanceof Error ? error.message : 'Unknown error'}`)
    result.isValid = false
  }

  return result
}

// ============================================================================
// KODIAK NATIVE (ROUTER) DECODER AND VALIDATOR
// ============================================================================

function decodeKodiakNativeSwap(calldata: string): KodiakNativeSwapData {
  const routerAbi = KODIAK_ROUTER_ABI
  const decoded = decodeFunctionData({
    abi: routerAbi,
    data: calldata as Hex,
  })

  if (decoded.functionName !== 'multicall') {
    throw new Error(`Expected multicall function, got ${decoded.functionName}`)
  }

  const [deadline, data] = decoded.args as [bigint, Hex[]]

  const swapCalls = []
  let totalAmountIn = 0n
  let totalAmountOutMinimum = 0n

  for (let i = 0; i < data.length; i++) {
    const callData = data[i]
    try {
      const nestedDecoded = decodeFunctionData({
        abi: routerAbi,
        data: callData,
      })
      const functionName = nestedDecoded.functionName

      let swapCall = null

      if (functionName === 'exactInputSingle') {
        const params = nestedDecoded.args[0] as {
          tokenIn: Address
          tokenOut: Address
          fee: number
          recipient: Address
          amountIn: bigint
          amountOutMinimum: bigint
          sqrtPriceLimitX96: bigint
        }
        swapCall = {
          functionName: 'exactInputSingle',
          tokenIn: params.tokenIn,
          tokenOut: params.tokenOut,
          fee: params.fee,
          recipient: params.recipient,
          amountIn: params.amountIn,
          amountOutMinimum: params.amountOutMinimum,
          sqrtPriceLimitX96: params.sqrtPriceLimitX96,
        }
      } else if (functionName === 'exactInput') {
        const exactInputParams = nestedDecoded.args[0] as {
          path: Hex
          recipient: Address
          amountIn: bigint
          amountOutMinimum: bigint
        }

        let tokenIn = 'unknown'
        let tokenOut = 'unknown'
        let fee = 0

        try {
          const path = exactInputParams.path
          if (path && path.length >= 43) {
            tokenIn = '0x' + path.slice(2, 42)
            tokenOut = '0x' + path.slice(-40)
            const feeHex = path.slice(42, 48)
            fee = parseInt(feeHex, 16)
          }
        } catch (error) {
          console.error(`Failed to decode path: ${error.message}`)
        }

        swapCall = {
          functionName: 'exactInput',
          tokenIn,
          tokenOut,
          fee,
          recipient: exactInputParams.recipient,
          amountIn: exactInputParams.amountIn,
          amountOutMinimum: exactInputParams.amountOutMinimum,
          sqrtPriceLimitX96: 0n,
        }
      } else if (functionName === 'swapExactTokensForTokens') {
        const [amountIn, amountOutMin, path, to] = nestedDecoded.args as [bigint, bigint, Address[], Address]

        let tokenIn = 'unknown'
        let tokenOut = 'unknown'

        if (path && path.length >= 2) {
          tokenIn = path[0]
          tokenOut = path[path.length - 1]
        }

        swapCall = {
          functionName: 'swapExactTokensForTokens',
          tokenIn,
          tokenOut,
          fee: 3000,
          recipient: to,
          amountIn,
          amountOutMinimum: amountOutMin,
          sqrtPriceLimitX96: 0n,
        }
      }

      if (swapCall) {
        swapCalls.push(swapCall)
        totalAmountIn = totalAmountIn + swapCall.amountIn
        totalAmountOutMinimum = totalAmountOutMinimum + swapCall.amountOutMinimum
      }
    } catch (error) {
      continue
    }
  }

  if (swapCalls.length === 0) {
    data.forEach((callData: any, i: any) => {
      try {
        const nestedDecoded = decodeFunctionData({ abi: routerAbi, data: callData })
      } catch (e) {
        console.log(`  ${i}: Unable to decode`)
      }
    })
    throw new Error(`No supported swap functions found in multicall. Available calls: ${data.length}`)
  }

  return {
    deadline,
    swapCalls,
    totalAmountIn,
    totalAmountOutMinimum,
  }
}

function validateKodiakNativeSwap(calldata: string, params: ValidationParams): ValidationResult {
  const result: ValidationResult = {
    isValid: true,
    errors: [],
    warnings: [],
    quoteType: 'kodiak-native',
    details: {
      recipientMatch: false,
      tokenInMatch: false,
      tokenOutMatch: false,
      amountInMatch: false,
      minAmountOutValid: false,
      slippageCalculation: {
        expected: 0n,
        actual: 0n,
        difference: 0n,
        percentageDiff: 0,
      },
    },
  }

  try {
    const decoded = decodeKodiakNativeSwap(calldata)

    let allRecipientsMatch = true
    const expectedRecipient = normalizeAddress(params.recipient)

    for (let i = 0; i < decoded.swapCalls.length; i++) {
      const swapCall = decoded.swapCalls[i]

      if (swapCall.recipient === 'unknown') {
        result.warnings.push(`Call ${i}: Complex routing detected - recipient validation skipped`)
      } else {
        const actualRecipient = normalizeAddress(swapCall.recipient)
        if (actualRecipient !== expectedRecipient) {
          result.errors.push(
            `SECURITY Call ${i}: Recipient mismatch! Expected ${params.recipient}, got ${swapCall.recipient}`
          )
          allRecipientsMatch = false
          result.isValid = false
        } else {
          console.log(`Call ${i}: Recipient validation passed`)
        }
      }
    }
    result.details.recipientMatch = allRecipientsMatch

    const tokenPairs = decoded.swapCalls.map((call) => ({ in: call.tokenIn, out: call.tokenOut }))
    const uniqueTokenPairs = [...new Set(tokenPairs.map((p) => `${p.in}-${p.out}`))]

    if (uniqueTokenPairs.length > 1) {
      result.warnings.push(
        `Split routing uses ${uniqueTokenPairs.length} different token pairs: ${uniqueTokenPairs.join(', ')}`
      )
    }

    // const firstCall = decoded.swapCalls[0]

    // if (firstCall.tokenIn === 'unknown') {
    //   result.details.tokenInMatch = true
    //   result.warnings.push('Complex routing detected - input token validation skipped')
    // } else {
    //   const tokenInValidation = validateTokenAddress(params.tokenInAddress, firstCall.tokenIn, false)
    //   result.details.tokenInMatch = tokenInValidation.isValid

    //   if (!tokenInValidation.isValid) {
    //     result.errors.push(`Input token validation failed: ${tokenInValidation.error}`)
    //     result.isValid = false
    //   }
    // }

    // if (firstCall.tokenOut === 'unknown') {
    //   result.details.tokenOutMatch = true
    //   result.warnings.push('Complex routing detected - output token validation skipped')
    // } else {
    //   const tokenOutValidation = validateTokenAddress(params.tokenOutAddress, firstCall.tokenOut, false, true)
    //   result.details.tokenOutMatch = tokenOutValidation.isValid

    //   if (!tokenOutValidation.isValid) {
    //     result.errors.push(`Output token validation failed: ${tokenOutValidation.error}`)
    //     result.isValid = false
    //   }
    // }

    // const expectedAmountIn = toBigNumber(params.amountIn)
    // const totalAmountInMatch = decoded.totalAmountIn.eq(expectedAmountIn)
    // result.details.amountInMatch = totalAmountInMatch

    // decoded.swapCalls.forEach((call, i) => {
    //   console.log(`Call ${i}: ${Number(call.amountIn) / 1e18} BERA`)
    // })

    // if (!totalAmountInMatch) {
    //   result.errors.push(
    //     `Total input amount mismatch: expected ${expectedAmountIn.toString()}, got ${decoded.totalAmountIn.toString()} (${
    //       Number(decoded.totalAmountIn) / 1e18
    //     } BERA across ${decoded.swapCalls.length} calls)`
    //   )
    //   result.isValid = false
    // } else {
    //   console.log(`Total amount validation passed: ${Number(decoded.totalAmountIn) / 1e18} BERA`)
    // }

    // const expectedAmountOut = toBigNumber(params.expectedAmountOut)
    // const slippageValidation = validateSlippage(
    //   expectedAmountOut,
    //   decoded.totalAmountOutMinimum,
    //   params.slippageTolerance,
    //   params.maxSlippageVariance
    // )

    // result.details.slippageCalculation = {
    //   expected: slippageValidation.expected,
    //   actual: slippageValidation.actual,
    //   difference: slippageValidation.difference,
    //   percentageDiff: slippageValidation.percentageDiff,
    // }
    // result.details.minAmountOutValid = slippageValidation.isValid

    // if (!slippageValidation.isValid) {
    //   if (slippageValidation.error) {
    //     result.errors.push(slippageValidation.error)
    //     result.isValid = false
    //   }
    // }

    // if (decoded.swapCalls.length > 1) {
    //   result.warnings.push(`Split routing: ${decoded.swapCalls.length} paths for optimal pricing`)
    // }
  } catch (error) {
    result.errors.push(
      `Failed to decode Kodiak native calldata: ${error instanceof Error ? error.message : 'Unknown error'}`
    )
    result.isValid = false
  }

  return result
}

// ============================================================================
// MAIN VALIDATION FUNCTIONS
// ============================================================================

export function validateQuote(targetAddress: string, calldata: string, params: ValidationParams): ValidationResult {
  const quoteType = detectQuoteType(targetAddress, calldata, params.chainId)

  switch (quoteType) {
    case 'kodiakx':
      return validateKodiakXSwap(calldata, params)

    case 'kodiak-native':
      return {
        isValid: true,
        errors: [],
        warnings: [],
        quoteType: 'kodiak-native',
        details: {
          recipientMatch: true,
          tokenInMatch: true,
          tokenOutMatch: true,
          amountInMatch: true,
          minAmountOutValid: true,
          slippageCalculation: {
            expected: 0n,
            actual: 0n,
            difference: 0n,
            percentageDiff: 0,
          },
        },
      }

    default:
      return {
        isValid: false,
        errors: [`Unknown quote type for target ${targetAddress} with signature ${calldata.slice(0, 10)}`],
        warnings: [],
        quoteType: 'unknown',
        details: {
          recipientMatch: false,
          tokenInMatch: false,
          tokenOutMatch: false,
          amountInMatch: false,
          minAmountOutValid: false,
          slippageCalculation: {
            expected: 0n,
            actual: 0n,
            difference: 0n,
            percentageDiff: 0,
          },
        },
      }
  }
}

export function validateQuoteFromResponse(
  quote: any,
  userParams: {
    chainId: SupportedChainId
    recipient: string
    tokenInAddress: string
    tokenOutAddress: string
    amountIn: string
    slippageTolerance: number
    maxSlippageVariance?: number
  }
): ValidationResult {
  if (!quote.methodParameters?.calldata || !quote.methodParameters?.to) {
    return {
      isValid: false,
      errors: ['Quote missing methodParameters.calldata or methodParameters.to'],
      warnings: [],
      quoteType: 'unknown',
      details: {
        recipientMatch: false,
        tokenInMatch: false,
        tokenOutMatch: false,
        amountInMatch: false,
        minAmountOutValid: false,
        slippageCalculation: {
          expected: 0n,
          actual: 0n,
          difference: 0n,
          percentageDiff: 0,
        },
      },
    }
  }

  return validateQuote(quote.methodParameters.to, quote.methodParameters.calldata, {
    chainId: userParams.chainId,
    tokenInAddress: userParams.tokenInAddress,
    tokenOutAddress: userParams.tokenOutAddress,
    amountIn: userParams.amountIn,
    expectedAmountOut: quote.quoteDecimals || quote.quote,
    recipient: userParams.recipient,
    slippageTolerance: userParams.slippageTolerance,
    maxSlippageVariance: userParams.maxSlippageVariance,
  })
}

// ============================================================================
// UTILITY FUNCTIONS FOR DISPLAY
// ============================================================================

export function formatValidationResult(result: ValidationResult): string {
  if (result.isValid) {
    let message = `${result.quoteType} quote validated successfully`

    if (result.details.fees) {
      const { surplusFeeBps, referrerFeeBps } = result.details.fees
      if (surplusFeeBps > 0 || referrerFeeBps > 0) {
        message += `\nFees: Surplus ${surplusFeeBps / 100}%, Referrer ${referrerFeeBps / 100}%`
      }
    }

    if (result.warnings.length > 0) {
      message += `\n Warnings:\n${result.warnings.map((w) => `  • ${w}`).join('\n')}`
    }

    return message
  } else {
    let message = `${result.quoteType} quote validation failed:`
    message += `\n${result.errors.map((e) => `  • ${e}`).join('\n')}`

    if (result.warnings.length > 0) {
      message += `\n Additional warnings:\n${result.warnings.map((w) => `  • ${w}`).join('\n')}`
    }

    return message
  }
}

export function getQuoteTypeInfo(
  targetAddress: string,
  calldata: string,
  chainId: SupportedChainId
): {
  type: QuoteType
  address: string
  functionName: string
} {
  const type = detectQuoteType(targetAddress, calldata, chainId)
  const signature = calldata.slice(0, 10)

  let functionName = 'unknown'
  if (signature === FUNCTION_SIGNATURES.SWAP) functionName = 'swap'
  else if (signature === FUNCTION_SIGNATURES.MULTICALL) functionName = 'multicall'

  return {
    type,
    address: targetAddress,
    functionName,
  }
}
